"use client";

import * as React from "react";
import { Command as CommandPrimitive } from "cmdk";
import { Search } from "lucide-react";

import { cn } from "@/lib/utils";

function Command({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive>): React.ReactElement {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        "flex size-full flex-col rounded-lg bg-popover text-popover-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

/** Row with optional leading icon for the palette input inside a dialog. */
function CommandInputLeading({
  className,
  children,
  icon,
  ...props
}: React.ComponentProps<"div"> & {
  icon?: React.ReactElement;
}) {
  return (
    <div
      data-slot="command-input-wrapper"
      className={cn(
        "flex items-center gap-2 border-b px-3 [--cmdk-input-wrapper-height:_3rem]",
        className,
      )}
      {...props}
    >
      {icon ?? <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />}
      {children}
    </div>
  );
}

function CommandInput({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) {
  return (
    <>
      {/* cmdk nests the input */}
      <CommandPrimitive.Input
        data-slot="command-input"
        className={cn(
          "outline-hidden placeholder:text-muted-foreground aria-invalid:placeholder:text-destructive flex h-11 grow rounded-md bg-transparent py-3 text-base disabled:opacity-50 sm:text-sm [&_kbd]:rounded [&_kbd]:bg-muted [&_kbd]:px-1.5 [&_kbd]:text-[10px] [&_kbd]:font-medium [&_kbd]:text-muted-foreground",
          className,
        )}
        {...props}
      />
    </>
  );
}

/** Use inside `Command` with Search left; puts input in cmdk-compatible wrapper visually. */
function CommandInputBar({
  className,
  inputClassName,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input> & {
  inputClassName?: string;
}) {
  return (
    <CommandInputLeading className={className}>
      <CommandInput className={cn("flex-1", inputClassName)} {...props} />
    </CommandInputLeading>
  );
}

function CommandList({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn(
        "max-h-[min(55vh,20rem)] scroll-py-1 overflow-x-hidden overflow-y-auto p-1",
        className,
      )}
      {...props}
    />
  );
}

function CommandEmpty({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className={cn("rounded-md px-4 py-6 text-center text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        "-mx-1 flex flex-col gap-1 px-2 py-1",
        "[&_[cmdk-group-items]]:flex [&_[cmdk-group-items]]:flex-col [&_[cmdk-group-items]]:gap-1",
        className,
      )}
      {...props}
    />
  );
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>): React.ReactElement {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={cn("-mx-1 h-px bg-border", className)}
      {...props}
    />
  );
}

function CommandItem({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>): React.ReactElement {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        "relative flex cursor-default select-none items-center gap-3 rounded-lg px-2 py-2.5 text-sm outline-none",
        "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-45",
        "data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground",
        "data-[option-selected=true]:bg-primary/10 data-[option-selected=true]:font-medium data-[option-selected=true]:text-foreground",
        "data-[selected=true]:data-[option-selected=true]:bg-primary/15",
        "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
        className,
      )}
      {...props}
    />
  );
}

export {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandInputBar,
  CommandInputLeading,
  CommandItem,
  CommandList,
  CommandSeparator,
};
